import { useMemo } from 'react';
import { crmRatesOnFile, crmRelationship, useOutreachRows, type CrmRatesOnFile } from '@discovr/features';
import type { OutreachRow } from '@discovr/types';
import { DataTable, type ColumnDef } from '../../../components/common/DataTable';
import { cn } from '../../../lib/utils';
import { ratesOnFileLabel } from './crmFormat';
import { audienceSourceLabel, channelColumn, creatorColumn, crmRowLabel, lastActivityColumn } from './crmCreatorColumns';
import { CrmCreatorRowMenu } from './CrmCreatorRowMenu';
import { CrmListEmpty } from './CrmListEmpty';
import { CrmListFrame } from './CrmListFrame';
import { useCrmListPaging, type CrmCreatorsListProps } from './useCrmListPaging';
import { CrmRelationshipPill } from './CrmStagePill';
import { OutreachConfirmDialog } from './OutreachConfirmDialog';
import { useCrmSuppression, type CrmSuppression } from './useCrmSuppression';
import { useOpenThreadRow } from './useOpenThreadRow';

/** Figma mutes an empty Rates on file so the rows with rates stand out. */
const RATES_ON_FILE_TONE: Record<CrmRatesOnFile['kind'], string> = {
  locked: 'text-foreground',
  quoted: 'text-foreground',
  none: 'text-muted-foreground',
};

function RatesOnFileCell({ row }: { row: OutreachRow }) {
  const onFile = crmRatesOnFile(row);
  return <span className={cn('text-body-sm-regular', RATES_ON_FILE_TONE[onFile.kind])}>{ratesOnFileLabel(onFile)}</span>;
}

function allCreatorsColumns(suppression: CrmSuppression): ColumnDef<OutreachRow>[] {
  return [
    creatorColumn<OutreachRow>(),
    channelColumn,
    {
      id: 'audience_source',
      header: 'Audience source',
      minWidth: 150,
      cell: (row) => <span className="text-body-sm-regular text-foreground">{audienceSourceLabel(row.audienceSource, row.importSource)}</span>,
    },
    {
      id: 'relationship',
      header: 'Relationship',
      minWidth: 140,
      skeletonShape: 'pill',
      cell: (row) => <CrmRelationshipPill relationship={crmRelationship(row)} />,
    },
    {
      id: 'rates_on_file',
      header: 'Rates on file',
      minWidth: 110,
      cell: (row) => <RatesOnFileCell row={row} />,
    },
    lastActivityColumn,
    {
      id: 'actions',
      header: '',
      minWidth: 56,
      align: 'right',
      alwaysVisible: true,
      cell: (row) => <CrmCreatorRowMenu row={row} suppression={suppression} />,
    },
  ];
}

/** Every creator in this campaign's outreach. A row opens its conversation once there is one. */
export function CrmAllCreatorsTable({ campaignId, query, onClearSearch }: CrmCreatorsListProps) {
  const paging = useCrmListPaging();
  const { data, isLoading, error } = useOutreachRows(campaignId, {
    tab: 'everyone',
    q: query || undefined,
    sort: 'last_activity',
    page: paging.page,
    limit: paging.pageSize,
  });
  const openIfThread = useOpenThreadRow();
  const { suppression, dialog } = useCrmSuppression(campaignId);
  const columns = useMemo(() => allCreatorsColumns(suppression), [suppression]);

  return (
    <>
      <CrmListFrame error={error} total={data?.total ?? 0} paging={paging}>
        <DataTable<OutreachRow>
          columns={columns}
          data={data?.rows ?? []}
          getRowId={(row) => row.entryId}
          loading={isLoading}
          onRowClick={openIfThread}
          rowAriaLabel={crmRowLabel}
          emptyState={<CrmListEmpty view="all" query={query} onClearSearch={onClearSearch} />}
        />
      </CrmListFrame>
      <OutreachConfirmDialog {...dialog} />
    </>
  );
}
